import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { navCategories } from "@/mocks/categories";

const navPaths: Record<string, string> = {
  首页: "/",
  关于: "/about",
};

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);

  const goSearch = (e: FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    navigate(kw ? `/search?q=${encodeURIComponent(kw)}` : "/search");
    setMenuOpen(false);
  };

  const navItems = [
    { name: "首页", to: "/" },
    ...navCategories.map((c) => ({ name: c.name, to: `/category/${c.slug}` })),
    { name: "关于", to: navPaths.关于 },
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="mx-auto flex h-16 w-full items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-book-open-line text-xl" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-display text-lg font-extrabold tracking-tight text-foreground-950">
              韩漫免费看
            </span>
            <span className="mt-0.5 hidden text-[10px] tracking-wide text-foreground-500 sm:block">
              在线阅读漫画
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) => {
            const active =
              item.to === "/"
                ? location.pathname === "/"
                : location.pathname.startsWith(item.to);
            return (
              <Link
                key={item.name}
                to={item.to}
                className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  active
                    ? "bg-primary-50 text-primary-700"
                    : "text-foreground-700 hover:bg-background-100 hover:text-primary-700"
                }`}
              >
                {item.name}
              </Link>
            );
          })}
        </nav>

        <form
          onSubmit={goSearch}
          className="ml-auto hidden max-w-xs flex-1 items-center md:flex"
        >
          <div className="flex w-full items-center gap-2 rounded-full border border-background-200 bg-background-100 px-3 py-1.5 focus-within:border-primary-300 focus-within:bg-background-50">
            <i className="ri-search-line text-base text-foreground-400" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索韩漫标题或作者"
              aria-label="搜索韩漫"
              className="w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </div>
        </form>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setMenuOpen((v) => !v)}
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-md border border-background-200 text-foreground-700 md:ml-0 lg:hidden"
        >
          <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-4 lg:hidden">
          <form onSubmit={goSearch} className="mb-4 md:hidden">
            <div className="flex items-center gap-2 rounded-full border border-background-200 bg-background-100 px-3 py-2">
              <i className="ri-search-line text-base text-foreground-400" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索韩漫标题或作者"
                aria-label="搜索韩漫"
                className="w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
              />
            </div>
          </form>
          <div className="flex flex-wrap gap-2">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.to}
                onClick={() => setMenuOpen(false)}
                className="whitespace-nowrap rounded-md bg-background-100 px-3 py-2 text-sm font-medium text-foreground-700"
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}